/**
 * The ONE duration formatter for every surface that shows how long something
 * took (run durations, latencies, percentiles, stage timings).
 *
 * Rule (product ask, 2026-10-05): people read seconds, never milliseconds, and
 * once a duration reaches a minute it reads as minutes and seconds. The output
 * never contains "ms". Callers hold milliseconds (the stored unit) and pass
 * them straight in; nothing else in the app should build a duration string.
 *
 *   null / NaN / <0 -> "—"
 *   0        -> "0s"
 *   4        -> "<0.01s"
 *   70       -> "0.07s"      (under 1 s: two decimals)
 *   450      -> "0.45s"
 *   2092     -> "2.1s"       (1 s to under 1 min: one decimal)
 *   36839    -> "36.8s"
 *   59950    -> "1m"         (never "60.0s")
 *   95000    -> "1m 35s"     (a zero seconds part is dropped: "2m")
 *   3720000  -> "1h 2m"      (a zero minutes part is dropped: "1h")
 *
 * No space between a number and its unit, one space between parts.
 */
export function fmtDurationMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  if (ms === 0) return '0s';
  if (ms < 10) return '<0.01s';
  // Round first, then pick the rung, so a value never prints at the top of a
  // rung it has rounded out of ("1.00s", "60.0s").
  const hundredths = Math.round(ms / 10);
  if (hundredths < 100) return `${(hundredths / 100).toFixed(2)}s`;
  const tenths = Math.round(ms / 100);
  if (tenths < 600) return `${(tenths / 10).toFixed(1)}s`;
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}

/** Same ladder but from seconds (some rows carry duration in seconds). */
export function fmtDurationSec(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return '—';
  return fmtDurationMs(sec * 1000);
}
